/**
 * 工作流 — 多步 Agent 流程可视化编排
 */

import React, { useState } from 'react';

interface WorkflowBlock {
  id: string;
  type: 'trigger' | 'agent' | 'condition' | 'output';
  label: string;
  emoji: string;
}

const TEMPLATES = [
  {
    name: '智能客服流程',
    emoji: '💬',
    steps: ['接入渠道 → ', '意图识别 → ', '回答生成 → ', '满意度检测'],
  },
  {
    name: '营销内容创作',
    emoji: '✨',
    steps: ['灵感采集 → ', '文案撰写 → ', '风格润色 → ', '发布排期'],
  },
  {
    name: '数据分析报告',
    emoji: '📊',
    steps: ['数据导入 → ', '异常检测 → ', '洞察总结 → ', '周报生成'],
  },
];

export default function WorkflowsPage() {
  const [blocks] = useState<WorkflowBlock[]>([
    { id: '1', type: 'trigger', label: '触发器', emoji: '⚡' },
    { id: '2', type: 'agent', label: 'AI Agent', emoji: '🤖' },
    { id: '3', type: 'condition', label: '条件分支', emoji: '🔀' },
    { id: '4', type: 'output', label: '输出结果', emoji: '📤' },
  ]);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>⚙️ 工作流</h1>
        <p style={styles.subtitle}>编排多 Agent 协作流程，让复杂任务自动化运行</p>
        <button style={styles.createBtn}>+ 创建工作流</button>
      </div>

      {/* Workflow Canvas */}
      <div style={styles.canvas}>
        <div style={styles.canvasHeader}>
          <span style={styles.canvasTitle}>工作流编排画布</span>
          <span style={styles.canvasHint}>拖拽下方组件到画布</span>
        </div>

        {/* Block Palette */}
        <div style={styles.palette}>
          {blocks.map((block) => (
            <div key={block.id} style={styles.paletteItem}>
              <span style={styles.paletteEmoji}>{block.emoji}</span>
              <span style={styles.paletteLabel}>{block.label}</span>
            </div>
          ))}
        </div>

        <div style={styles.divider} />

        {/* Canvas Area */}
        <div style={styles.canvasArea}>
          {/* Workflow Chain */}
          <div style={styles.chain}>
            <span style={styles.chainArrow}>⚡</span>
            <div style={styles.chainBlock}>
              <span style={styles.chainEmoji}>⚡</span>
              <span style={styles.chainName}>触发器</span>
              <span style={styles.chainDesc}>接收用户消息</span>
            </div>
            <div style={styles.chainConnector} />
            <div style={styles.chainBlock}>
              <span style={styles.chainEmoji}>🤖</span>
              <span style={styles.chainName}>AI Agent</span>
              <span style={styles.chainDesc}>理解并处理任务</span>
            </div>
            <div style={styles.chainConnector} />
            <div style={styles.chainBlock}>
              <span style={styles.chainEmoji}>📤</span>
              <span style={styles.chainName}>输出结果</span>
              <span style={styles.chainDesc}>返回给用户</span>
            </div>
          </div>

          <div style={styles.runBar}>
            <span style={styles.runInfo}>3 个节点 · 2 条连接</span>
            <button style={styles.runBtn}>▶ 运行工作流</button>
          </div>
        </div>
      </div>

      {/* Templates */}
      <div>
        <h2 style={styles.sectionTitle}>推荐模板</h2>
        <div style={styles.templateGrid}>
          {TEMPLATES.map((tmpl, idx) => (
            <div key={idx} style={styles.templateCard}>
              <span style={styles.templateEmoji}>{tmpl.emoji}</span>
              <h3 style={styles.templateName}>{tmpl.name}</h3>
              <div style={styles.templateSteps}>
                {tmpl.steps.map((step, i) => (
                  <span key={i} style={styles.templateStep}>{step}</span>
                ))}
              </div>
              <button style={styles.useTemplateBtn}>使用此模板</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 24 },
  header: { position: 'relative' as const },
  title: { fontSize: 22, fontWeight: 700, margin: 0 },
  subtitle: { fontSize: 12, color: '#64748b', margin: '4px 0 0' },
  createBtn: {
    position: 'absolute' as const, right: 0, top: 0,
    padding: '7px 16px', borderRadius: 8, border: 'none',
    background: '#6366f1', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  canvas: {
    borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)',
    background: 'rgba(255,255,255,0.025)', overflow: 'hidden' as const,
  },
  canvasHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)',
    background: 'rgba(255,255,255,0.02)',
  },
  canvasTitle: { fontSize: 13, fontWeight: 600 },
  canvasHint: { fontSize: 10, color: '#64748b' },
  palette: { display: 'flex', gap: 8, padding: '12px 16px' },
  paletteItem: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
    padding: '10px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)',
    background: 'rgba(255,255,255,0.02)', cursor: 'grab',
  },
  paletteEmoji: { fontSize: 16 },
  paletteLabel: { fontSize: 10, color: '#94a3b8' },
  divider: { height: 1, background: 'rgba(255,255,255,0.05)' },
  canvasArea: { padding: 16, minHeight: 120 },
  chain: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    flexWrap: 'wrap' as const, marginBottom: 12,
  },
  chainArrow: { fontSize: 10, opacity: 0 },
  chainBlock: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
    padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(99,102,241,0.08)', minWidth: 80,
  },
  chainEmoji: { fontSize: 20 },
  chainName: { fontSize: 11, fontWeight: 600 },
  chainDesc: { fontSize: 9, color: '#64748b', textAlign: 'center' as const },
  chainConnector: { width: 20, height: 2, background: 'rgba(255,255,255,0.1)' },
  runBar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '8px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.02)',
  },
  runInfo: { fontSize: 10, color: '#64748b' },
  runBtn: {
    padding: '5px 14px', borderRadius: 6, border: 'none',
    background: '#34d399', color: '#0f172a', fontSize: 11, fontWeight: 600, cursor: 'pointer',
  },
  sectionTitle: { fontSize: 15, fontWeight: 600, margin: '0 0 10px' },
  templateGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 },
  templateCard: {
    padding: 16, borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)',
    background: 'rgba(255,255,255,0.025)',
  },
  templateEmoji: { fontSize: 24, display: 'block', marginBottom: 8 },
  templateName: { fontSize: 14, fontWeight: 600, margin: '0 0 8px' },
  templateSteps: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 },
  templateStep: { fontSize: 11, color: '#94a3b8' },
  useTemplateBtn: {
    padding: '5px 12px', borderRadius: 6, border: '1px solid #6366f1',
    background: 'transparent', color: '#a5b4fc', fontSize: 11, fontWeight: 600, cursor: 'pointer',
  },
};
